<template>
  <div>
    <BaseHeader
      :title="name"
      :style-variant="2"
    >
      <template
        v-if="!isPlatformBottimmo"
        v-slot:design
      >
        <HeaderDesign />
      </template>
      <template v-slot:desc>
        <p>
          {{ t('landingpages.guide.subtitle') }}
        </p>
      </template>
      <template v-slot:content>
        <v-breadcrumbs :items="breadcrumbItems" />
      </template>
    </BaseHeader>
    <v-row>
      <v-col
        cols="12"
        lg="4"
        class="pr-lg-0 pb-0 pb-lg-3"
      >
        <v-card class="wrapper-card border-top-mobile">
          <v-card class="d-lg-none">
            <Results
              :conversionCount="conversionCount"
              :type="landingpageType"
            />
          </v-card>
          <v-card class="h-full step-card mb-2">
            <StepOne
              :landingpage-slug="slug"
              :readable-prefix="readablePrefix"
              :readable-landingpage="name"
              :company-slug="companySlug"
              :company-site-url="companySiteUrl"
              :landingpage-type="landingpageType"
            />
          </v-card>
          <v-card class="h-full step-card mb-2">
            <StepTwo
              :original-url="originalUrl"
              :pretty-original-url="prettyOriginalUrl"
              :custom-url="customUrl"
              :prettyCustomUrl="prettyCustomUrl"
            />
          </v-card>
          <v-card class="h-full step-card step-card-mobile mb-2">
            <Step
              :step="{
                title: t('landingpages.steps.step-3.title'),
                content: t('landingpages.steps.step-3.content'),
              }"
            />
          </v-card>
          <v-card class="d-none d-lg-block">
            <Results
              :conversionCount="conversionCount"
              :type="landingpageType"
            />
          </v-card>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        lg="8"
        class="pt-0 pt-lg-3"
      >
        <StepThree />
      </v-col>
    </v-row>
  </div>
</template>
<script lang="ts" setup>
import { useApolloClient } from '@vue/apollo-composable'
import { computed, inject, onMounted, Ref, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { AuthInstance } from '@/auth'
import BaseHeader from '@/components/BaseHeader.vue'
import { useBranding } from '@/composables/useBranding'
import { TOOLS } from '@/lib/tools'
import COMPANY_BASE from '@/modules/landingpage/CompanyBase.gql'
import LANDINGPAGE from '@/modules/landingpage/Landingpage.gql'
import { Company } from '@/modules/landingpage/models/Company'
import { LandingPage } from '@/modules/landingpage/models/LandingPage'
import Results from '@/modules/landingpage/steps/Results.vue'
import Step from '@/modules/landingpage/steps/Step.vue'
import StepOne from '@/modules/landingpage/steps/StepOne.vue'
import StepThree from '@/modules/landingpage/steps/StepThree.vue'
import StepTwo from '@/modules/landingpage/steps/StepTwo.vue'
import HeaderDesign from '@/modules/landingpages/HeaderDesign.vue'

const { isPlatformBottimmo } = useBranding()
const ARTICLE: Record<string, string> = {
  VALUATION: 'landingpage.integration.prefix.valuation',
  RETURN_CALL: 'landingpage.integration.prefix.return-call',
  LIFE_ANNUITY: 'landingpage.integration.prefix.life-annuity',
  PARTIAL_SALE: 'landingpage.integration.prefix.partial-sale',
  PLOT_VALUATION: 'landingpage.integration.prefix.plot-valuation',
  QUIZZARD: 'landingpage.integration.prefix.quizzard',
  GUIDE: 'landingpage.integration.prefix.guide',
  CHECKLIST: 'landingpage.integration.prefix.checklist',
}

const route = useRoute()

const { t } = useI18n()
const apollo = useApolloClient()
const $auth = inject<AuthInstance>('auth')

const landingpage: Ref<LandingPage | null> = ref(null)
const company: Ref<Company | null> = ref(null)

onMounted(() => {
  fetchLandingPage()
  fetchCompany()
})

const conversionCount = computed(() => landingpage.value?.conversionCount || 0)
const originalUrl = computed(() => landingpage.value?.originalUrl || '')
const prettyOriginalUrl = computed(() => originalUrl.value.replace(/^(https?:\/\/)/, ''))
const prettyCustomUrl = computed(() => {
  const customUrlValue = landingpage.value?.customUrl || ''
  return customUrlValue.replace(/^(https?:\/\/)?/, '')
})
const customUrl = computed(() => landingpage.value?.customUrl || '')
const name = computed(() => landingpage.value?.name || 'Landingpage')
const slug = computed(() => landingpage.value?.slug || '')
const landingpageType = computed(() => landingpage.value?.type || 'GUIDE')
const companySlug = computed(() => company.value?.slug || '')
const companySiteUrl = computed(() => company.value?.site?.url || '')
const breadcrumbItems = computed(() => {
  const type =
    landingpageType.value === 'GUIDE'
      ? 'guides'
      : landingpageType.value === 'CHECKLIST'
        ? 'checklists'
        : TOOLS.find((tool) => tool.type === landingpageType.value)
          ? 'tools'
          : 'campaigns'

  return [
    {
      title: t('landingpages.guide.breadcrumb.landingpages'),
      to: `/landingpages#${type}`,
      disabled: false,
      exact: true,
    },
    { title: t('landingpages.guide.breadcrumb.details') },
  ]
})

const readablePrefix = computed(() => {
  const translationKey = ARTICLE[landingpageType.value]
  if (!translationKey) return ''
  try {
    return t(translationKey)
  } catch {
    return ''
  }
})

const fetchLandingPage = async () => {
  const { data } = await apollo.client.query({
    query: LANDINGPAGE,
    variables: { input: { landingpageSlug: route.params.slug } },
  })
  landingpage.value = data.landingpage
}

const fetchCompany = async () => {
  const { data } = await apollo.client.query({
    query: COMPANY_BASE,
    variables: { id: $auth.user?.companyId },
  })
  company.value = data.company
}
</script>

<style scoped>
.wrapper-card {
  padding: 15px;
  background-color: white;
}

.step-card {
  padding: 10px 15px 15px 15px;
  background-color: #175e51;
  border-radius: 10px;
  color: white;
}

.step-card-mobile {
  display: none;
}

.v-card .v-card-title {
  line-height: 0;
}

@media (min-width: 961px) {
  .wrapper-card {
    border: 1px solid #e4e4e4;
    border-radius: 10px !important;
  }
}

@media (max-width: 960px) {
  .wrapper-card {
    border-top: 1px solid #e4e4e4;
    border-left: 1px solid #e4e4e4;
    border-right: 1px solid #e4e4e4;
    border-bottom: none;
    border-radius: 0;
    padding-bottom: 0;
  }

  .border-top-mobile {
    border-radius: 8px 8px 0 0 !important;
  }

  .step-card-mobile {
    display: block;
  }
}
</style>
